import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Database, Search, ShieldCheck, ChevronRight, ChevronDown, Loader2,
  Table2, Hash, Type, Ruler, Shield, Boxes, ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { PiiConsistencyDialog } from '@/components/semantic-models/PiiConsistencyDialog';
import { ConditionalFormulaBreakdown } from '@/components/ConditionalFormulaBreakdown';
import { isConditionalFormula } from '@/lib/conditionalFormula';

const statusStyle = (s) => {
  const v = (s || '').toLowerCase();
  if (v === 'certified') return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20';
  if (v === 'published' || v === 'active') return 'bg-blue-500/15 text-blue-400 border-blue-500/20';
  return 'bg-amber-500/15 text-amber-400 border-amber-500/20';
};

function highlightText(text, q) {
  if (!q || !text) return text;
  const idx = text.toLowerCase().indexOf(q);
  if (idx === -1) return text;
  return (<>{text.slice(0, idx)}<mark className="bg-amber-300/50 text-foreground rounded px-0.5">{text.slice(idx, idx + q.length)}</mark>{text.slice(idx + q.length)}</>);
}

function FieldRow({ attr, q }) {
  const isMeasure = attr.semantic_type === 'measure';
  const label = attr.business_name || attr.display_name || attr.name;
  const conditional = isConditionalFormula(attr.formula);
  const matched = !!q && ((label || '').toLowerCase().includes(q) || (attr.name || '').toLowerCase().includes(q));
  return (
    <div className={`px-3 py-1.5 border-b border-border/10 last:border-0 ${matched ? 'bg-amber-400/10 ring-1 ring-inset ring-amber-400/30' : ''}`} data-testid={`catalog-field-${attr.name}`} data-matched={matched ? 'true' : undefined}>
      <div className="grid grid-cols-[1.6fr_0.9fr_0.9fr] gap-2 items-center">
        <div className="flex items-center gap-2 min-w-0">
          {isMeasure ? <Ruler className="w-3.5 h-3.5 text-primary/70 flex-shrink-0" /> : <Type className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />}
          <span className="text-xs text-foreground truncate" title={label}>{matched ? highlightText(label, q) : label}</span>
          {conditional && <Badge className="text-[8px] bg-indigo-500/15 text-indigo-400 border-indigo-500/20">Conditional</Badge>}
          {attr.pii && <Badge className="text-[8px] bg-rose-500/15 text-rose-400 border-rose-500/20 gap-1"><Shield className="w-2.5 h-2.5" />PII</Badge>}
        </div>
        <span className="text-[11px] text-muted-foreground capitalize truncate">{attr.semantic_type || '—'}</span>
        <span className="text-[11px] text-muted-foreground truncate">{attr.data_type || '—'}</span>
      </div>
      {conditional && <ConditionalFormulaBreakdown formula={attr.formula} compact testId={`catalog-cond-${attr.name}`} />}
    </div>
  );
}

function ModelCard({ model, query, defaultOpen }) {
  const [open, setOpen] = useState(false);
  const [entities, setEntities] = useState(null);
  const [measures, setMeasures] = useState(null);
  const [loading, setLoading] = useState(false);
  const [piiOpen, setPiiOpen] = useState(false);

  const loadDetail = useCallback(async (force = false) => {
    if (!force && entities !== null) return;
    setLoading(true);
    try {
      const [res, msrs] = await Promise.all([
        api.getSemanticEntities(model.model_id),
        api.getSemanticMeasures(model.model_id).catch(() => []),
      ]);
      setEntities(Array.isArray(res) ? res : []);
      setMeasures(Array.isArray(msrs) ? msrs : []);
    } catch (e) {
      toast.error(e.message || 'Could not load tables');
      setEntities([]);
      setMeasures([]);
    } finally {
      setLoading(false);
    }
  }, [entities, model.model_id]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) loadDetail();
  };

  // Auto-expand + load when the model matched only via a table/field search.
  useEffect(() => {
    if (defaultOpen) { setOpen(true); loadDetail(); }
  }, [defaultOpen, loadDetail]);

  const q = (query || '').trim().toLowerCase();
  const certified = (model.certification && model.certification.status) === 'certified';

  return (
    <div className="rounded-xl border border-border/60 bg-card overflow-hidden" data-testid={`catalog-model-${model.model_id}`}>
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-muted/30 transition-colors"
        data-testid={`catalog-model-toggle-${model.model_id}`}
      >
        <div className="mt-0.5 text-muted-foreground">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</div>
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Boxes className="w-4.5 h-4.5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-heading font-semibold text-foreground truncate">{model.name}</h3>
            <Badge className={`text-[9px] border ${statusStyle(model.status)}`}>{model.status || 'draft'}</Badge>
            {certified && <Badge className="text-[9px] bg-emerald-500/15 text-emerald-400 border-emerald-500/20 gap-1"><ShieldCheck className="w-2.5 h-2.5" />Certified</Badge>}
          </div>
          {model.description && <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{model.description}</p>}
          {model.source_name && (
            <p className="text-[11px] text-muted-foreground/80 mt-1 flex items-center gap-1"><Database className="w-3 h-3" /> {model.source_name}</p>
          )}
        </div>
      </button>

      {open && (
        <div className="border-t border-border/40 bg-muted/10 px-4 py-3">
          {loading && <div className="flex items-center gap-2 text-xs text-muted-foreground py-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading tables…</div>}
          {!loading && entities && (
            <div className="flex justify-end mb-2">
              <Button variant="outline" size="sm" className="rounded-lg text-xs h-7 gap-1.5"
                onClick={() => setPiiOpen(true)} data-testid={`catalog-fix-pii-${model.model_id}`}>
                <ShieldAlert className="w-3.5 h-3.5 text-amber-500" />
                Fix PII Inconsistencies
              </Button>
            </div>
          )}
          {!loading && (() => {
            const calc = (measures || []).filter((m) => m.formula);
            const shownCalc = q ? calc.filter((m) => (m.business_name || m.name || '').toLowerCase().includes(q) || (m.formula || '').toLowerCase().includes(q)) : calc;
            if (shownCalc.length === 0) return null;
            return (
              <div className="mb-3 rounded-lg border border-indigo-500/20 bg-card overflow-hidden" data-testid={`catalog-calc-measures-${model.model_id}`}>
                <div className="flex items-center gap-2 px-3 py-2 bg-indigo-500/10 border-b border-indigo-500/20">
                  <Ruler className="w-3.5 h-3.5 text-indigo-400" />
                  <span className="text-xs font-semibold text-foreground">Calculated measures</span>
                  <span className="text-[10px] text-muted-foreground ml-auto">{shownCalc.length}</span>
                </div>
                <div>
                  {shownCalc.map((m) => {
                    const conditional = isConditionalFormula(m.formula);
                    return (
                      <div key={m.measure_id || m.name} className="px-3 py-1.5 border-b border-border/10 last:border-0" data-testid={`catalog-calc-${m.name}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <Ruler className="w-3.5 h-3.5 text-indigo-400/70 flex-shrink-0" />
                          <span className="text-xs text-foreground truncate" title={m.business_name || m.name}>{m.business_name || m.name}</span>
                          {conditional && <Badge className="text-[8px] bg-indigo-500/15 text-indigo-400 border-indigo-500/20">Conditional</Badge>}
                          {(m.certification && m.certification.status === 'certified') && <Badge className="text-[8px] bg-emerald-500/15 text-emerald-400 border-emerald-500/20 gap-1"><ShieldCheck className="w-2.5 h-2.5" />Certified</Badge>}
                        </div>
                        <p className="text-[10px] font-mono text-amber-400/90 mt-0.5 break-words">{m.formula}</p>
                        {conditional && <ConditionalFormulaBreakdown formula={m.formula} compact testId={`catalog-cond-${m.name}`} />}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          {!loading && entities && entities.length === 0 && (
            <p className="text-xs text-muted-foreground py-2">No tables in this model yet.</p>
          )}
          {!loading && entities && entities.length > 0 && (
            <div className="space-y-3">
              {entities.map((en) => {
                const attrs = (en.attributes || []).filter((a) => {
                  if (!q) return true;
                  const label = (a.business_name || a.display_name || a.name || '').toLowerCase();
                  return label.includes(q) || (a.name || '').toLowerCase().includes(q);
                });
                if (q && attrs.length === 0 && !(en.name || '').toLowerCase().includes(q)) return null;
                const shown = (q && !(en.name || '').toLowerCase().includes(q)) ? attrs : (en.attributes || []);
                return (
                  <div key={en.entity_id} className="rounded-lg border border-border/40 bg-card overflow-hidden" data-testid={`catalog-table-${en.entity_id}`}>
                    <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 border-b border-border/30">
                      <Table2 className="w-3.5 h-3.5 text-primary/80" />
                      <span className="text-xs font-semibold text-foreground truncate">{en.name}</span>
                      {en.entity_type && <Badge className="text-[8px] bg-muted text-muted-foreground border-border/40 capitalize">{en.entity_type}</Badge>}
                      <span className="text-[10px] text-muted-foreground ml-auto flex items-center gap-1"><Hash className="w-3 h-3" />{(en.attributes || []).length} fields</span>
                    </div>
                    <div>
                      {shown.length === 0 && <p className="px-3 py-2 text-[11px] text-muted-foreground">No fields.</p>}
                      {shown.map((a, i) => <FieldRow key={`${a.name}-${i}`} attr={a} q={q} />)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      <PiiConsistencyDialog
        modelId={model.model_id}
        modelName={model.name}
        open={piiOpen}
        onOpenChange={setPiiOpen}
        onApplied={() => loadDetail(true)}
      />
    </div>
  );
}

export default function DataCatalogPage() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [fieldIndex, setFieldIndex] = useState({}); // model_id -> lowercase table+field blob

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getSemanticModels();
      setModels(Array.isArray(res) ? res : []);
    } catch (e) {
      toast.error(e.message || 'Failed to load data catalog');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Load a lightweight table+field index so search can surface a model whose
  // TABLE or FIELD matches (not just the model's own name).
  useEffect(() => {
    api.getSemanticModelsFieldIndex()
      .then((r) => {
        const map = {};
        (r.index || []).forEach((it) => { map[it.model_id] = it.hay || ''; });
        setFieldIndex(map);
      })
      .catch(() => {});
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return models;
    return models.filter((m) => {
      const hay = `${m.name || ''} ${m.description || ''} ${m.source_name || ''}`.toLowerCase();
      return hay.includes(q) || (fieldIndex[m.model_id] || '').includes(q);
    });
  }, [models, q, fieldIndex]);
  // A model matched only via a table/field (not its own name) → auto-expand it.
  const matchedByField = (m) => {
    if (!q) return false;
    const nameHay = `${m.name || ''} ${m.description || ''} ${m.source_name || ''}`.toLowerCase();
    return !nameHay.includes(q) && (fieldIndex[m.model_id] || '').includes(q);
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-8" data-testid="data-catalog-page">
      <div className="mb-6">
        <h1 className="text-3xl font-heading font-bold text-foreground tracking-tight flex items-center gap-3">
          <Database className="w-7 h-7 text-primary" /> Data Catalog
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Browse every governed data asset in one place — semantic models, their tables and business fields,
          with certification status and PII flags. Search across models and fields by their business names.
        </p>
      </div>

      <div className="relative mb-6">
        <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search models, tables and fields…"
          className="pl-9"
          data-testid="catalog-search"
        />
      </div>

      {loading && (
        <div className="space-y-3" data-testid="catalog-loading">
          {[0, 1, 2].map((i) => <div key={i} className="h-16 rounded-xl border border-border/40 bg-muted/20 animate-pulse" />)}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="text-center py-16 border border-dashed border-border/50 rounded-xl" data-testid="catalog-empty">
          <Database className="w-8 h-8 text-muted-foreground/50 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">{q ? 'No data assets match your search.' : 'No semantic models available yet.'}</p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <>
          <p className="text-xs text-muted-foreground mb-3" data-testid="catalog-count">{filtered.length} data asset{filtered.length === 1 ? '' : 's'}</p>
          <div className="space-y-3">
            {filtered.map((m) => <ModelCard key={m.model_id} model={m} query={query} defaultOpen={matchedByField(m)} />)}
          </div>
        </>
      )}
    </div>
  );
}
